import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useIsMaster } from '../../common/useIsMaster';
import HardDeleteDialog from '../common/HardDeleteDialog';
import { useDispatch } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import PageSubtitle from '../../components/PageSubtitle';
import CustomButton from '../../components/CustomButton';
import InputField from '../../components/ui/InputField';
import AssetDetailSection from '../assets/components/AssetDetailSection';
import DetailField from '../assets/components/DetailField';
import { paths } from '../../routes';
import {
    invalidateInventoryReaders,
    inventoryApiSlice,
    useDeleteHouseMutation,
    useGetStationQuery,
    useRemoveStationLocationImageMutation,
    useUpdateStationLocationMutation,
    useUpdateStationMutation,
    useUploadStationLocationImageMutation,
} from '../../api/inventory/inventoryApiSlice';
import { locationsApiSlice } from '../../api/locations/locationsApiSlice';
import { readInventoryFailure } from '../../api/inventory/inventoryErrors';
import type { StationDetailResponse, UpdateStationLocationRequest } from '../../api/station/stationApiTypes';
import StationLocationSection, {
    LOCATION_PHOTO_MIME,
    MAX_LOCATION_PHOTO_BYTES,
} from './components/StationLocationSection';
import {
    type LocationDraft,
    locationDraftChanged,
    locationDraftFrom,
    locationPatchOf,
} from './stationLocationDraft';
import { coordinatesState } from '../../utils/coordinates';
import StationObjectsSection from './components/StationObjectsSection';
import StationOpeningHoursSection from './components/StationOpeningHoursSection';
import StationProductDefaultsSection from './components/StationProductDefaultsSection';

/**
 * A Station after it exists — read and edit (collector #2050 item 22).
 *
 * ── Where it lives, and what it is deliberately NOT behind ─────────────────
 *
 * Under **Objekte**, reached by clicking a station in the Objektübersicht. NOT
 * under Zugangsverwaltung: that route is entitlement-gated, and a station is
 * inventory that exists whether or not the tenant holds the access-management
 * add-on (PO, 2026-09-10). A tenant with numbered compartments and no smart
 * locks has stations and must be able to edit them.
 *
 * ── The rule the whole screen serves ───────────────────────────────────────
 *
 * **An edit affects bookings going forward and never a historic one.** Most of
 * that is structural rather than enforced — `Order.location_id` is stored on
 * the order, so a booking keeps the location it was made at, and `depot_id`
 * identifies the same station under any name, which is why renaming a station
 * with bookings is allowed (PO, 2026-09-10). The one place it needs enforcing
 * is capacity, and that guard is server-side in
 * `new-backend/admin/src/lib/inventory/stationEdit.ts` — this screen only
 * explains it.
 */
/** A house's description is an internal note: it never reaches customers. */
const descriptionLabelKey = (isHouse: boolean) => (isHouse
    ? 'station_detail.fields.house_description'
    : 'station_detail.fields.description');

type Unwrappable = { unwrap: () => Promise<unknown> };

/**
 * The location half of the page's one Speichern: the route body for what
 * changed, then the status toggles, then the photo — each only when changed.
 */
const saveLocationDraft = async (params: {
    stationId: string;
    location: NonNullable<StationDetailResponse['location']>;
    draft: LocationDraft;
    addressFixed: boolean;
    updateLocation: (arg: { stationId: string } & UpdateStationLocationRequest) => Unwrappable;
    updateStatus: (arg: { locationId: string, data: { status: { enabled: boolean, bookable: boolean } } }) => Unwrappable;
    uploadPhoto: (arg: { stationId: string, file: File }) => Unwrappable;
    removePhoto: (arg: { stationId: string }) => Unwrappable;
}): Promise<boolean> => {
    const { stationId, location, draft, addressFixed } = params;
    const patch = locationPatchOf(draft, location, addressFixed);
    if (patch) await params.updateLocation({ stationId, ...patch }).unwrap();
    const status = draft.status;
    const statusChanged = !!status && !!location.status
        && (status.enabled !== location.status.enabled || status.bookable !== location.status.bookable);
    // The route replaces BOTH flags, so both are sent.
    if (statusChanged) await params.updateStatus({ locationId: location.id, data: { status } }).unwrap();
    if (draft.photoFile) {
        await params.uploadPhoto({ stationId, file: draft.photoFile }).unwrap();
    } else if (draft.removePhoto && location.images?.[0]) {
        await params.removePhoto({ stationId }).unwrap();
    }
    return statusChanged;
};

/** Positions and buffer: whole, not negative, buffer within positions. */
const countsAreValid = (doorCount: number, buffer: number): boolean =>
    Number.isInteger(doorCount) && doorCount >= 0
    && Number.isInteger(buffer) && buffer >= 0
    && buffer <= doorCount;

/** The location draft can be sent: a complete address where it is editable, never half a pin. */
const locationDraftValid = (
    draft: LocationDraft, location: StationDetailResponse['location'], addressFixed: boolean,
): boolean => {
    if (!location) return true;
    if (coordinatesState(draft.pin) === 'incomplete') return false;
    return addressFixed || [draft.street, draft.postalCode, draft.city].every((part) => part.trim().length > 0);
};

/** Whether the station's own fields differ from what is stored. */
const stationFieldsChanged = (
    station: StationDetailResponse | undefined,
    fields: { name: string, description: string, doorCount: number, bufferDoorsCount: number },
): boolean => !!station && (
    fields.name.trim() !== (station.name ?? '').trim()
    || fields.description.trim() !== (station.description ?? '').trim()
    || fields.doorCount !== station.doorCount
    || fields.bufferDoorsCount !== station.bufferDoorsCount
);

/** A failed save as one line: the server's blockers when it named any. */
const saveErrorText = (error: unknown, t: (key: string, options?: Record<string, unknown>) => string): string => {
    const failure = readInventoryFailure(error);
    return failure.blockers?.length
        ? failure.blockers
            .map((blocker) => t(`station_detail.blockers.${blocker}`, {
                defaultValue: t('station_detail.blockers.unknown'),
            }))
            .join(' ')
        : failure.message ?? t('station_detail.save_error');
};

const StationDetailPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { stationId = '' } = useParams<{ stationId: string }>();
    const navigate = useNavigate();
    const isMaster = useIsMaster();
    const [deleteHouse] = useDeleteHouseMutation();
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);

    const { data: station, isLoading, isError, refetch } = useGetStationQuery(
        { stationId },
        { skip: !stationId },
    );
    const [updateStation, { isLoading: isSavingStation }] = useUpdateStationMutation();
    const [updateLocation, { isLoading: isSavingLocation }] = useUpdateStationLocationMutation();
    const [updateStatus, { isLoading: isSavingStatus }] = locationsApiSlice.useUpdateDepotStatusMutation();
    const [uploadPhoto, { isLoading: isUploadingPhoto }] = useUploadStationLocationImageMutation();
    const [removePhoto, { isLoading: isRemovingPhoto }] = useRemoveStationLocationImageMutation();
    const isSaving = isSavingStation || isSavingLocation || isSavingStatus || isUploadingPhoto || isRemovingPhoto;

    const [editing, setEditing] = useState(false);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [doorCount, setDoorCount] = useState('');
    const [bufferDoorsCount, setBufferDoorsCount] = useState('');
    const [saveError, setSaveError] = useState<string | null>(null);
    // ONE "Bearbeiten" for the page (PO, 2026-10-19): the location and its
    // opening hours are edited with the station, saved by the same Speichern.
    const [locationDraft, setLocationDraft] = useState<LocationDraft>(() => locationDraftFrom(null));
    const [hoursValid, setHoursValid] = useState(true);
    const [photoError, setPhotoError] = useState<string | null>(null);
    // The picked photo's preview; revoked when replaced or when the page goes.
    const pickedPhotoUrl = useMemo(
        () => (locationDraft.photoFile ? URL.createObjectURL(locationDraft.photoFile) : null),
        [locationDraft.photoFile],
    );
    useEffect(() => () => {
        if (pickedPhotoUrl) URL.revokeObjectURL(pickedPhotoUrl);
    }, [pickedPhotoUrl]);

    /**
     * Seed the form from the payload at the moment editing STARTS, not in an
     * effect watching the query.
     *
     * An effect would be a second writer racing the refetch a save triggers,
     * which is the shape CLAUDE.md documents ("useEffect: Separate Init and
     * Reaction") and which cost a follow-up in the SUP carousel. Opening the
     * form is a discrete event; seeding on that event has exactly one writer.
     */
    const startEditing = useCallback(() => {
        if (!station) return;
        setName(station.name ?? '');
        setDescription(station.description ?? '');
        setDoorCount(String(station.doorCount ?? 0));
        setBufferDoorsCount(String(station.bufferDoorsCount ?? 0));
        setLocationDraft(locationDraftFrom(station.location));
        setHoursValid(true);
        setPhotoError(null);
        setSaveError(null);
        setEditing(true);
    }, [station]);

    /**
     * Why capacity cannot be reduced right now, if it cannot.
     *
     * Said BEFORE the operator tries, not as a 409 afterwards. The backend
     * refuses in the safe direction — a reduction needs the station to carry no
     * commitment at all — so the form has to explain a rule that is stricter
     * than "the maths works out".
     */
    const capacityLocked = useMemo(() => {
        if (!station) return false;
        return station.commitments.liveHolders > 0 || station.commitments.futureBookings > 0;
    }, [station]);

    const parsedDoorCount = Number(doorCount);
    const parsedBuffer = Number(bufferDoorsCount);
    const countsValid = countsAreValid(parsedDoorCount, parsedBuffer);
    const addressFixed = !station?.locationEditable;
    const stationChanged = stationFieldsChanged(station, {
        name, description, doorCount: parsedDoorCount, bufferDoorsCount: parsedBuffer,
    });
    // Speichern needs a change and a valid form (design-decisions › Bearbeiten und Speichern).
    const canSave = !isSaving && name.trim().length > 0 && countsValid && hoursValid
        && locationDraftValid(locationDraft, station?.location ?? null, addressFixed)
        && (stationChanged || locationDraftChanged(locationDraft, station?.location ?? null, addressFixed));

    const handlePhotoPicked = useCallback((file: File) => {
        if (!LOCATION_PHOTO_MIME.has(file.type.toLowerCase())) {
            setPhotoError(t('station_detail.location.photo_invalid_type'));
            return;
        }
        if (file.size > MAX_LOCATION_PHOTO_BYTES) {
            setPhotoError(t('station_detail.location.photo_too_large'));
            return;
        }
        setPhotoError(null);
        setLocationDraft((draft) => ({ ...draft, photoFile: file, removePhoto: false }));
    }, [t]);

    /**
     * The one Speichern: the station's own fields, then its location (address,
     * district, pin, zone, hours), its status and its photo — each request
     * only when that part changed, in that order, stopping at the first
     * refusal so the page never reports a partial save as done.
     */
    const handleSave = useCallback(async () => {
        if (!station || !canSave) return;
        setSaveError(null);
        try {
            if (stationChanged) {
                await updateStation({
                    stationId,
                    name: name.trim(),
                    description: description.trim(),
                    // Sent only when actually changed: an unchanged capacity must
                    // not walk into the server's reduction guard.
                    ...(parsedDoorCount !== station.doorCount ? { doorCount: parsedDoorCount } : {}),
                    ...(parsedBuffer !== station.bufferDoorsCount
                        ? { bufferDoorsCount: parsedBuffer }
                        : {}),
                }).unwrap();
            }
            if (station.location) {
                const statusChanged = await saveLocationDraft({
                    stationId, location: station.location, draft: locationDraft, addressFixed,
                    updateLocation, updateStatus, uploadPhoto, removePhoto,
                });
                if (statusChanged) dispatch(inventoryApiSlice.util.invalidateTags([{ type: 'Station', id: stationId }]));
            }
            invalidateInventoryReaders(dispatch);
            setEditing(false);
        } catch (error) {
            setSaveError(saveErrorText(error, t));
        }
    }, [
        station, canSave, stationChanged, updateStation, stationId, name, description,
        parsedDoorCount, parsedBuffer, locationDraft, addressFixed, updateLocation,
        updateStatus, uploadPhoto, removePhoto, dispatch, t,
    ]);

    if (isLoading) {
        return (
            <div className={'p-6'}>
                <div className={'flex justify-center py-10'}>
                    <div className={'animate-spin rounded-full h-10 w-10 border-b-2 border-primary'} />
                </div>
            </div>
        );
    }

    // A failed load is never an empty page: "we could not load it" and "there
    // is nothing here" must not look alike (CLAUDE.md).
    if (isError || !station) {
        return (
            <div className={'p-6'}>
                <PageHeader>
                    <PageHeadline>{t('station_detail.title')}</PageHeadline>
                </PageHeader>
                <div className={'max-w-2xl rounded-[5px] bg-text-contrast p-5 shadow-sm'}>
                    <p className={'text-sm text-foreground'}>{t('station_detail.load_error')}</p>
                    <div className={'mt-4 flex gap-2'}>
                        <CustomButton title={t('common.reload')} onClick={() => refetch()} />
                        <Link to={paths.assets} className={'text-sm text-primary hover:underline self-center'}>
                            {t('station_detail.back_to_objects')}
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const bookablePositions = Math.max(0, station.doorCount - station.bufferDoorsCount);
    // A house holds rooms and has no doors: no position fields, its own words.
    const isHouse = station.isHouse;

    return (
        <div className={'p-6 flex flex-col gap-4'}>
            <PageHeader>
                <div className={'min-w-0'}>
                    <PageHeadline>{station.name}</PageHeadline>
                    <PageSubtitle>
                        {isHouse
                            ? t('station_detail.house_subtitle', { count: station.objectCount })
                            : t('station_detail.subtitle', {
                                objects: station.objectCount,
                                positions: station.doorCount,
                            })}
                    </PageSubtitle>
                </div>
                {!editing && (
                    <div className={'flex items-center gap-2'}>
                        {/* An action, not an edit: master only, for a house
                            (PO, 2026-10-19). The server refuses one that still
                            holds rooms and says so in the dialog. */}
                        {isMaster && isHouse && (
                            <CustomButton
                                title={t('station_detail.delete.action')}
                                isFilled={false}
                                onClick={() => setIsDeleteOpen(true)}
                            />
                        )}
                        <CustomButton title={t('common.edit')} onClick={startEditing} />
                    </div>
                )}
            </PageHeader>

            <AssetDetailSection
                title={isHouse ? t('station_detail.sections.house') : t('station_detail.sections.station')}
            >
                {editing ? (
                    <div className={'flex flex-col gap-4 max-w-xl'}>
                        <InputField
                            label={isHouse ? t('station_detail.fields.house_name') : t('station_detail.fields.name')}
                            value={name}
                            onChange={setName}
                        />
                        <InputField
                            label={t(descriptionLabelKey(isHouse))}
                            value={description}
                            onChange={setDescription}
                        />
                        {!isHouse && (
                            <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                                <InputField
                                    label={t('station_detail.fields.positions')}
                                    value={doorCount}
                                    onChange={setDoorCount}
                                    type={'number'}
                                    min={0}
                                    disabled={capacityLocked}
                                />
                                <InputField
                                    label={t('station_detail.fields.buffer')}
                                    value={bufferDoorsCount}
                                    onChange={setBufferDoorsCount}
                                    type={'number'}
                                    min={0}
                                    disabled={capacityLocked}
                                />
                            </div>
                        )}
                        {!isHouse && capacityLocked && (
                            <p className={'text-sm text-foreground/60'}>
                                {t('station_detail.capacity_locked', {
                                    live: station.commitments.liveHolders,
                                    future: station.commitments.futureBookings,
                                })}
                            </p>
                        )}
                        {!countsValid && (
                            <p className={'text-sm text-red-500'}>
                                {t('station_detail.validation.counts')}
                            </p>
                        )}
                    </div>
                ) : (
                    <div className={'grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl'}>
                        <DetailField
                            label={isHouse ? t('station_detail.fields.house_name') : t('station_detail.fields.name')}
                            value={station.name}
                        />
                        <DetailField
                            label={t(descriptionLabelKey(isHouse))}
                            value={station.description}
                        />
                        {!isHouse && (
                            <>
                                <DetailField
                                    label={t('station_detail.fields.positions')}
                                    value={station.doorCount}
                                />
                                <DetailField
                                    label={t('station_detail.fields.buffer')}
                                    value={station.bufferDoorsCount}
                                />
                                <DetailField
                                    label={t('station_detail.fields.bookable_positions')}
                                    value={bookablePositions}
                                />
                            </>
                        )}
                        {/* The room count is the header's (PO, 2026-10-19: once). */}
                        {!isHouse && (
                            <DetailField
                                label={t('station_detail.fields.objects')}
                                value={station.objectCount}
                            />
                        )}
                    </div>
                )}
            </AssetDetailSection>

            {/* A station: its positions and their products first — "what is in
                this station" is the question an operator opens the page with,
                and #2280's answer, several products in one station, lives
                there. A house: what is maintained AT the house first — location,
                then its opening hours — and the rooms last (PO, 2026-10-19). */}
            {!isHouse && <StationObjectsSection station={station} />}
            {/* D15 (product refactor 4.4): per product, the limit and the
                checklist that apply here — the product's, or the station's
                own. Not for a room or a house: a room's Group carries neither. */}
            {!isHouse && !station.isRoomStation && <StationProductDefaultsSection stationId={station.id} />}
            <StationLocationSection
                station={station}
                mode={editing ? 'edit' : 'view'}
                draft={locationDraft}
                onDraftChange={setLocationDraft}
                pickedPhotoUrl={pickedPhotoUrl}
                photoError={photoError}
                onPhotoPicked={handlePhotoPicked}
            />
            <StationOpeningHoursSection
                station={station}
                mode={editing ? 'edit' : 'view'}
                hours={locationDraft.hours}
                onHoursChange={(hours) => setLocationDraft((draft) => ({ ...draft, hours }))}
                onValidityChange={setHoursValid}
            />
            {isHouse && <StationObjectsSection station={station} />}

            <p className={'text-sm text-foreground/60'}>
                <Link to={paths.assets} className={'text-primary hover:underline'}>
                    {t('station_detail.back_to_objects')}
                </Link>
            </p>

            {isDeleteOpen && (
                <HardDeleteDialog
                    name={station.name}
                    i18nPrefix={'station_detail.delete'}
                    run={() => deleteHouse({ stationId }).unwrap()}
                    onClose={() => setIsDeleteOpen(false)}
                    onDeleted={() => {
                        invalidateInventoryReaders(dispatch);
                        navigate(paths.assets);
                    }}
                />
            )}

            {/* The one Save/Cancel bar, as on the room page. */}
            {editing && (
                <div className={'sticky bottom-0 bg-text-contrast border-t border-foreground/10 px-6 py-3'
                    + ' flex items-center justify-end gap-3 shadow-lg z-10 -mx-6 -mb-6'}
                >
                    {saveError && <p className={'mr-auto text-sm text-error'} role={'alert'}>{saveError}</p>}
                    <CustomButton
                        title={t('common.cancel')}
                        isFilled={false}
                        onClick={() => setEditing(false)}
                        disabled={isSaving}
                    />
                    <CustomButton
                        title={t('common.save')}
                        onClick={handleSave}
                        disabled={!canSave}
                        loading={isSaving}
                    />
                </div>
            )}
        </div>
    );
};

export default StationDetailPage;
